// Generated from Swagger on 2025-08-27T14:46:24.222Z
import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
  UseSuspenseQueryOptions,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { useContext } from "react";
import { useApiClient } from "../client";
import { creationEntitiesKeys } from "../creation-entities-keys";
import { formatSearchQuery } from "../../utils/format-search-query";
import { Context } from "@/util-components/root/context";
import {
  PlayerListPaginatedResponseDtoWithListEntities,
  ApplyPlayerImageEditRequestDto,
  CreatePlayerRequestDto,
  ManagedImageResponseDto,
  PlayerManagedImageResponseDto,
  PlayerResponseDto,
  PlayerCreationEntitiesResponseDto,
  PlayerBirthdaysListPaginatedResponseDto,
  PlayerDetailsResponseDto,
  UpdatePlayerRequestDto,
  UPlayersuploadImageFormData,
} from "../interfaces/players.interface";

export interface UsePlayersFindParams {
  /**
   * The text to search for
   * @constraints minLength: 4, example: Jobdescription
   */
  textSearch?: string;
  /**
   * Team ID
   */
  teamId?: string;
  /**
   * Player position
   * @example Goalkeeper
   */
  position?: string;
  /**
   * Field to sort by
   * @example firstName
   */
  sort?: string;
  /**
   * Sort direction (asc or desc)
   * @example asc
   */
  order?: 'asc' | 'desc';
  /**
   * The maximum amount of retrieved items per page
   * @constraints minimum: 1, maximum: 20, default: 20, example: 20
   */
  limit?: number;
  /**
   * The page number to retrieve items from.
   * @constraints minimum: 1, default: 1, example: 1
   */
  page?: number;
}

export interface UsePlayersFindBirthdaysParams {
  /**
   * The maximum amount of retrieved items per page
   * @constraints minimum: 1, maximum: 20, default: 20, example: 20
   */
  limit?: number;
  /**
   * The page number to retrieve items from.
   * @constraints minimum: 1, default: 1, example: 1
   */
  page?: number;
}

// Query keys for players-related queries
export const playersKeys = {
  all: ["players"] as const,
  profile: () => [...playersKeys.all, "profile"] as const,
  mutation: () => [...playersKeys.all, "mutation"] as const,
  creation_entities: () => [...playersKeys.all, "creation_entities"] as const,
  birthdays: (params?: any) =>
    [...playersKeys.all, "birthdays", params] as const,
  details: () => [...playersKeys.all, "detail"] as const,
  detail: (id: string) => [...playersKeys.details(), id] as const,
  detail_mutation: (params?: any) =>
    [...playersKeys.all, "detail_mutation", params] as const,
  detail_image_mutation: (params?: any) =>
    [...playersKeys.all, "detail_image_mutation", params] as const,
  detail_secondary_teams_detail_mutation: (params?: any) =>
    [
      ...playersKeys.all,
      "detail_secondary_teams_detail_mutation",
      params,
    ] as const,
};

function invalidatePlayersQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: playersKeys.all });
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as string[];
      return queryKey.includes("creation-entities");
    },
  });
}

/**
 * Find all players with filter
 */
export function usePlayersFind(
  params?: UsePlayersFindParams,
  options?: Omit<
    UseSuspenseQueryOptions<PlayerListPaginatedResponseDtoWithListEntities>,
    "queryKey" | "queryFn"
  > & { skipGlobalErrorHandler?: boolean },
) {
  const { apiRequest } = useApiClient();
  const { skipGlobalErrorHandler, ...queryOptions } = options || {};

  return useSuspenseQuery({
    queryKey: [
      ...playersKeys.profile(),
      ...creationEntitiesKeys.list(),
      params,
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<PlayerListPaginatedResponseDtoWithListEntities>(
        `/players${queryString}`,
        "GET",
        undefined,
        undefined,
        { skipGlobalErrorHandler }
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...queryOptions,
  });
}

/**
 * Non-suspense version of usePlayersFind
 */
export function usePlayersFindQuery(
  params?: UsePlayersFindParams,
  options?: Omit<
    UseQueryOptions<PlayerListPaginatedResponseDtoWithListEntities>,
    "queryKey" | "queryFn"
  > & { skipGlobalErrorHandler?: boolean },
) {
  const { apiRequest } = useApiClient();
  const { skipGlobalErrorHandler, ...queryOptions } = options || {};

  return useQuery({
    queryKey: [
      ...playersKeys.profile(),
      ...creationEntitiesKeys.list(),
      params,
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<PlayerListPaginatedResponseDtoWithListEntities>(
        `/players${queryString}`,
        "GET",
        undefined,
        undefined,
        { skipGlobalErrorHandler }
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });
}

/**
 * Create a new player
 */
export function usePlayersCreate(
  options?: Omit<
    UseMutationOptions<PlayerResponseDto, Error, CreatePlayerRequestDto>,
    "mutationFn"
  > & { skipGlobalErrorHandler?: boolean },
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();
  const { refetchUser } = useContext(Context);
  const { skipGlobalErrorHandler, ...mutationOptions } = options || {};

  return useMutation({
    ...mutationOptions,
    mutationFn: (data: CreatePlayerRequestDto) =>
      apiRequest<PlayerResponseDto>(`/players`, "POST", data, undefined, {
        skipGlobalErrorHandler,
      }),
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      void refetchUser?.();
      mutationOptions?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Get creation entities
 */
export function usePlayersCreationEntities(
  options?: Omit<
    UseSuspenseQueryOptions<PlayerCreationEntitiesResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: creationEntitiesKeys.list(),
    queryFn: () =>
      apiRequest<PlayerCreationEntitiesResponseDto>(
        `/players/creation-entities`,
      ),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of usePlayersCreationEntities
 */
export function usePlayersCreationEntitiesQuery(
  options?: Omit<
    UseQueryOptions<PlayerCreationEntitiesResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: creationEntitiesKeys.list(),
    queryFn: () =>
      apiRequest<PlayerCreationEntitiesResponseDto>(
        `/players/creation-entities`,
      ),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Find players next birthdays
 */
export function usePlayersFindBirthdays(
  params?: UsePlayersFindBirthdaysParams,
  options?: Omit<
    UseSuspenseQueryOptions<PlayerBirthdaysListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [
      ...playersKeys.birthdays({ ...params }),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<PlayerBirthdaysListPaginatedResponseDto>(
        `/players/birthdays${queryString}`,
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of usePlayersFindBirthdays
 */
export function usePlayersFindBirthdaysQuery(
  params?: UsePlayersFindBirthdaysParams,
  options?: Omit<
    UseQueryOptions<PlayerBirthdaysListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [
      ...playersKeys.birthdays({ ...params }),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<PlayerBirthdaysListPaginatedResponseDto>(
        `/players/birthdays${queryString}`,
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Find one player
 */
export function usePlayersFindOne(
  id: string,
  options?: Omit<
    UseSuspenseQueryOptions<PlayerDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [...playersKeys.detail(id), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<PlayerDetailsResponseDto>(`/players/${id}`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of usePlayersFindOne
 */
export function usePlayersFindOneQuery(
  id: string,
  options?: Omit<
    UseQueryOptions<PlayerDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [...playersKeys.detail(id), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<PlayerDetailsResponseDto>(`/players/${id}`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Update one player
 */
export function usePlayersUpdate(
  options?: Omit<
    UseMutationOptions<
      any,
      Error,
      { id: string; data: UpdatePlayerRequestDto }
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, data }: { id: string; data: UpdatePlayerRequestDto }) =>
      apiRequest<any>(`/players/${id}`, "PATCH", data),
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete one player
 */
export function usePlayersDelete(
  options?: Omit<UseMutationOptions<any, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<any>(`/players/${id}`, "DELETE"),
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete multiple players
 */
export function usePlayersBulkDelete(
  options?: Omit<UseMutationOptions<any, Error, { ids: string[] }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ ids }: { ids: string[] }) =>
      apiRequest<any>(`/players/bulk-delete`, "POST", { ids }),
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Upload Player Image - Max 5MB
 */
export function usePlayersUploadImage(
  options?: Omit<
    UseMutationOptions<
      ManagedImageResponseDto,
      Error,
      { id: string; data: UPlayersuploadImageFormData }
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UPlayersuploadImageFormData;
    }) => {
      const formData = new FormData();
      if (data.file) formData.append("file", data.file);
      return apiRequest<ManagedImageResponseDto>(`/players/${id}/image`, "POST", formData);
    },
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export const usePlayersuploadImage = usePlayersUploadImage;

export function usePlayersUploadImageAsset(
  options?: Omit<
    UseMutationOptions<
      PlayerManagedImageResponseDto,
      Error,
      { id: string; data: UPlayersuploadImageFormData }
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UPlayersuploadImageFormData;
    }) => {
      const formData = new FormData();
      if (data.file) formData.append("file", data.file);
      return apiRequest<PlayerManagedImageResponseDto>(`/players/${id}/images`, "POST", formData);
    },
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete Player Image
 */
export function usePlayersDeleteImage(
  options?: Omit<UseMutationOptions<ManagedImageResponseDto, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<ManagedImageResponseDto>(`/players/${id}/image`, "DELETE"),
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function usePlayersApplyImageEdit(
  options?: Omit<
    UseMutationOptions<ManagedImageResponseDto, Error, { id: string; data: ApplyPlayerImageEditRequestDto }>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, data }: { id: string; data: ApplyPlayerImageEditRequestDto }) =>
      apiRequest<ManagedImageResponseDto>(`/players/${id}/image/edit`, "POST", data),
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function usePlayersRestoreOriginalImage(
  options?: Omit<UseMutationOptions<ManagedImageResponseDto, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<ManagedImageResponseDto>(`/players/${id}/image/restore-original`, "POST"),
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function usePlayersDeleteImageAsset(
  options?: Omit<UseMutationOptions<ManagedImageResponseDto, Error, { id: string; imageId: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, imageId }: { id: string; imageId: string }) =>
      apiRequest<ManagedImageResponseDto>(`/players/${id}/images/${imageId}`, "DELETE"),
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function usePlayersApplyImageAssetEdit(
  options?: Omit<
    UseMutationOptions<ManagedImageResponseDto, Error, { id: string; imageId: string; data: ApplyPlayerImageEditRequestDto }>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, imageId, data }: { id: string; imageId: string; data: ApplyPlayerImageEditRequestDto }) =>
      apiRequest<ManagedImageResponseDto>(`/players/${id}/images/${imageId}/edit`, "POST", data),
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function usePlayersRestoreOriginalImageAsset(
  options?: Omit<UseMutationOptions<ManagedImageResponseDto, Error, { id: string; imageId: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, imageId }: { id: string; imageId: string }) =>
      apiRequest<ManagedImageResponseDto>(`/players/${id}/images/${imageId}/restore-original`, "POST"),
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Add secondary team to player
 */
export function usePlayersaddSecondaryTeam(
  options?: Omit<
    UseMutationOptions<any, Error, { id: string; teamId: string }>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, teamId }: { id: string; teamId: string }) =>
      apiRequest<any>(`/players/${id}/secondary-teams/${teamId}`, "POST"),
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete secondary team from player
 */
export function usePlayersDeleteSecondaryTeam(
  options?: Omit<
    UseMutationOptions<any, Error, { id: string; teamId: string }>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, teamId }: { id: string; teamId: string }) =>
      apiRequest<any>(`/players/${id}/secondary-teams/${teamId}`, "DELETE"),
    onSuccess: (data, variables, context) => {
      invalidatePlayersQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function usePlayersPreviewBackgroundRemoval(
  options?: Omit<UseMutationOptions<Blob, Error, { file: File }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  
  return useMutation({
    ...options,
    mutationFn: async ({ file }: { file: File }) => {
      const formData = new FormData();
      formData.append("file", file);
      
      return apiRequest<Blob>(
        `/players/media/preview-background-removal`,
        "POST",
        formData,
        undefined,
        { responseType: "blob", throwOnError: true }
      );
    },
  });
}
